import { QueryClient } from '@tanstack/react-query';

/**
 * The app's one TanStack Query client (UI state plan, phase 0).
 *
 * Server data lives in this cache, in memory only: nothing is persisted, a
 * full page load starts empty. `ApiProvider` stays the HTTP client (tokens,
 * refresh, 401 / 403 handling); query functions call it.
 *
 * Defaults:
 * - `retry`: once, but never on 401 / 403 / 404 (a refusal or a missing record
 *   is an answer, not a failure to retry);
 * - `refetchOnWindowFocus: false` (today's behaviour: nothing reloads when the
 *   tab gets the focus back);
 * - `staleTime: 0`.
 */
export const NO_RETRY_STATUSES = [401, 403, 404];
export const MAX_RETRIES = 1;

/** The HTTP status an error or an ApiProvider answer carries, when it has one. */
export const statusOf = (error: unknown): number | null => {
  if (!error || typeof error !== 'object') return null;
  const e = error as Record<string, any>;
  for (const value of [e.status, e.httpStatus, e.statusCode, e.code, e.response?.status]) {
    const status = Number(value);
    if (Number.isInteger(status) && status >= 100 && status <= 599) return status;
  }
  return null;
};

export const shouldRetry = (failureCount: number, error: unknown): boolean => {
  const status = statusOf(error);
  if (status !== null && NO_RETRY_STATUSES.includes(status)) return false;
  return failureCount < MAX_RETRIES;
};

export const createQueryClient = (): QueryClient =>
  new QueryClient({
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        refetchOnWindowFocus: false,
        staleTime: 0,
      },
    },
  });

export const queryClient = createQueryClient();

// Dev builds only (plan decision D4): the TanStack Query devtools browser
// extension finds the client here. Never set in demo / production builds.
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as any).__TANSTACK_QUERY_CLIENT__ = queryClient;
}
